import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useColorModeValue, useTheme } from '@chakra-ui/react';
import { toZonedTime } from 'date-fns-tz';
import Highcharts from 'highcharts';
import { cloneDeep } from 'lodash';
import moment from 'moment';

import { COLOURS } from 'clipsal-cortex-utils/src/constants/colors';
import { formatDate, formatTime } from 'clipsal-cortex-utils/src/formatting/formatting';

import { COMMON_CHART_DATETIME_FORMATS } from '../../../../../common/constants';
import { useSiteCircuits } from '../../../../site/meter-setup/meterSetupApi';
import { DATA_TYPE_TO_VALUE_SUFFIX } from '../meter-config-constants';
import { buildLongMetrologyData, buildShortMetrologyData } from '../meter-config-helpers';
import { LongPowerDataType, ShortPowerDataType, SiteMeterData } from '../meter-config-types';
import { useAssignments, useLongIntervalMetrology, useShortIntervalMetrology } from '../meterConfigApi';

export type IntervalType = 'Short Energy' | 'Long Energy';

export const INITIAL_STATE = {
  selectedInterval: 'Short Energy' as IntervalType,
  selectedShortPowerDataType: 'pRealKw' as ShortPowerDataType,
  selectedLongPowerDataType: 'pRealKw' as LongPowerDataType,
  selectedDate: new Date(),
  hiddenCircuitIndexes: [] as number[],
  timezone: 'Australia/Sydney',
};

export const useMeterChartConfig = ({ siteDeviceId, siteId }: SiteMeterData) => {
  const { siteCircuits, isLoadingSiteCircuits } = useSiteCircuits({ siteId, skip: !siteId });
  const { isLoading: isLoadingAssignments } = useAssignments();

  // Only select circuits that match the siteDeviceId
  const circuits = useMemo(() => {
    return siteCircuits
      .filter((c) => c.site_device_id === siteDeviceId)
      .sort((a, b) => a.oem_circuit_id.localeCompare(b.oem_circuit_id));
  }, [siteCircuits, siteDeviceId]);

  const [
    {
      selectedInterval,
      selectedShortPowerDataType,
      selectedLongPowerDataType,
      selectedDate,
      hiddenCircuitIndexes,
      timezone,
    },
    setState,
  ] = useState(INITIAL_STATE);
  const isShortEnergySelected = selectedInterval === 'Short Energy';
  const theme = useTheme();
  const chartLabelColor = useColorModeValue(theme.colors.gray[500], theme.colors.gray[400]);
  const {
    data: longMetrologyData,
    refetch: refetchLongMetroLogyData,
    isFetching: isFetchingLongMetroLogyData,
    isLoading: isLoadingLongMetroLogyData,
  } = useLongIntervalMetrology(
    siteDeviceId,
    { reading_date: formatDate(selectedDate), timezone },
    selectedInterval === 'Short Energy'
  );
  const { data: shortMetrologyData, isLoading: isLoadingShortMetroLogyData } = useShortIntervalMetrology(
    siteDeviceId,
    !siteDeviceId || selectedInterval === 'Long Energy'
  );

  const chartRef = useRef<{
    chart: Highcharts.Chart;
    container: React.RefObject<HTMLDivElement>;
  } | null>(null);
  // I wasted far too long debugging strange UI issues to figure out the React Highcharts wrapper breaks basic React
  // core principles by mutating the chart props object passed to it directly (apparently for "performance reasons").
  // Because of this, we have to deep clone the state object being passed to the chart, otherwise countless weird bugs
  // end up happening.
  // see: https://github.com/highcharts/highcharts-react#why-highcharts-mutates-my-data

  const checkDateBeforeToday = (date: Date) => date <= toZonedTime(new Date(), timezone);

  const toggleLineState = useCallback(
    (channelIndex: number) => {
      if (hiddenCircuitIndexes.includes(channelIndex)) {
        setState((prevState) => ({
          ...prevState,
          hiddenCircuitIndexes: hiddenCircuitIndexes.filter((key) => key !== channelIndex),
        }));
      } else {
        setState((prevState) => ({
          ...prevState,
          hiddenCircuitIndexes: [...hiddenCircuitIndexes, channelIndex],
        }));
      }
    },
    [hiddenCircuitIndexes]
  );

  const { seriesData, chartOptions, latestCommunicatedTime, longChartData, shortChartData } = useMemo(() => {
    const longChartData = buildLongMetrologyData(longMetrologyData, circuits);

    const shortChartData = buildShortMetrologyData(shortMetrologyData, circuits);

    const seriesData = cloneDeep(
      isShortEnergySelected ? shortChartData[selectedShortPowerDataType] : longChartData[selectedLongPowerDataType]
    );

    const chartOptions = {
      title: {
        text: selectedInterval,
        style: { color: chartLabelColor },
      },
      legend: {
        enabled: true,
        verticalAlign: 'top',
        itemStyle: { color: chartLabelColor },
      },
      yAxis: {
        title: {
          text: seriesData.name,
          style: { color: chartLabelColor },
        },
        labels: {
          style: { color: chartLabelColor },
          formatter: function () {
            return (
              this.value +
              ` ${
                DATA_TYPE_TO_VALUE_SUFFIX[
                  isShortEnergySelected ? selectedShortPowerDataType : selectedLongPowerDataType
                ]
              }`
            );
          },
        },
      },
      xAxis: {
        type: 'datetime',
        dateTimeLabelFormats: COMMON_CHART_DATETIME_FORMATS,
        labels: {
          style: { color: chartLabelColor },
        },
      },
      time: {
        moment,
        timezone,
      },
      tooltip: {
        animation: false,
        shared: true,
        dateTimeLabelFormats: COMMON_CHART_DATETIME_FORMATS,
      },
      series: seriesData.data.map((series, index) => {
        return {
          ...series,
          visible: !hiddenCircuitIndexes.includes(index),
        };
      }),
      colors: COLOURS,
      chart: {
        zoomType: 'x',
        height: '400px',
        backgroundColor: 'transparent',
      },
      credits: {
        enabled: false,
      },
      plotOptions: {
        series: {
          marker: {
            enabled: false,
          },
          events: {
            legendItemClick: function (e: Event) {
              e.preventDefault();
              toggleLineState(this.index);
            },
          },
        },
      },
    };

    let latestCommunicatedTime = 'N/A';
    const latestTime = seriesData.data[0].data[seriesData.data[0].data.length - 1]?.x;
    if (latestTime) latestCommunicatedTime = formatTime(toZonedTime(new Date(latestTime), timezone));

    return { seriesData, chartOptions, latestCommunicatedTime, longChartData, shortChartData };
  }, [
    longMetrologyData,
    circuits,
    shortMetrologyData,
    isShortEnergySelected,
    selectedShortPowerDataType,
    selectedLongPowerDataType,
    selectedInterval,
    chartLabelColor,
    timezone,
    hiddenCircuitIndexes,
    toggleLineState,
  ]);

  function handleToggleLine(channelIndex: number) {
    if (chartRef.current)
      chartRef.current.chart.series[channelIndex].setVisible(!chartRef.current.chart.series[channelIndex].visible);
    toggleLineState(channelIndex);
  }

  return {
    seriesData,
    chartOptions,
    selectedInterval,
    selectedShortPowerDataType,
    selectedLongPowerDataType,
    selectedDate,
    hiddenCircuitIndexes,
    timezone,
    latestCommunicatedTime,
    longChartData,
    shortChartData,
    handleToggleLine,
    checkDateBeforeToday,
    refetchLongMetroLogyData,
    isFetchingLongMetroLogyData,
    chartRef,
    setState,
    isShortEnergySelected,
    circuits,
    isLoadingMetrologyData: isLoadingShortMetroLogyData || isLoadingLongMetroLogyData,
    isLoadingSiteCircuits,
    isLoadingAssignments,
  };
};
